import { makeContentContext } from '@objectiv/schema';
import { useVisibleEventTracker } from '@objectiv/tracker-react';
import { type ComponentType } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useUrlChange } from './useUrlChange';

export type Step<Slug = string> = {
  Page: ComponentType;
  slug: Slug;
};

const PAGEVIEW_CONTEXT_ID = 'pageview';
const PAGE_LOADED_CONTEXT_ID = 'pageloaded';

export const matchSteps = (steps: Step[]) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const pathSegments = currentPath.split('/').filter(Boolean);
  return steps.some(({ slug }) => {
    const slugSegments = slug.split('/').filter(Boolean).length;
    // Compare only the last segments of the current path to match the slug
    const pathToMatch = `/${pathSegments.slice(-slugSegments).join('/')}`;

    return !!matchPath({ path: slug, end: true }, pathToMatch);
  });
};

/** Hook that calls pageview and pageloaded events based on URL change
 * Takes steps as input to check if current URL is part of the steps
 */
export const usePageEventTracker = (step: Step[]) => {
  const stepMatch = matchSteps(step);

  const trackPageview = useVisibleEventTracker({
    locationStack: [makeContentContext({ id: PAGEVIEW_CONTEXT_ID })],
  });

  const trackPageLoaded = useVisibleEventTracker({
    locationStack: [makeContentContext({ id: PAGE_LOADED_CONTEXT_ID })],
  });

  useUrlChange(trackPageview, trackPageLoaded, stepMatch);
};
